import { create } from 'zustand'

import { uploadFile } from '@jotta/grpc-connect-client/useFileUpload'
import {
  AllocateConflictHandler,
  type AllocateConflictHandler as AllocateConflictHandlerType,
} from '@jotta/grpc-connect-openapi/file'
import { ErrorType } from '@jotta/grpc-web/no/jotta/openapi/error_pb'
import { AbortError } from '@jotta/types/AbortError'
import type { AppError } from '@jotta/types/AppError'
import { handleUnknownError } from '@jotta/types/AppError'
import type { FileMeta } from '@jotta/utils/files'
import { createPath } from '@jotta/utils/pathUtils'
import { nextXid } from '@jotta/utils/xid'

import type {
  ConflictResponse,
  FileConflictType,
} from '../conflictQueueAdapter'
import { createUploadConflictQueue } from '../conflictQueueAdapter'
import type {
  UploadFilter,
  UploadItem,
  UploadScope,
  UploadStatus,
} from '../types'

const uploadConflicts = createUploadConflictQueue()

type ScopeRuntime = {
  readonly limit: number
  running: number
  queued: string[]
}

type EnqueueUploadsInput = {
  apiPath: string
  files: FileMeta[]
  scope: UploadScope
  formatErrorMessage?: (error: AppError) => string
  token?: string
  openItem?: (filePath: string) => void
  onCompleted?: () => void
}

const scopeRuntimes: Record<UploadScope, ScopeRuntime> = {
  PRIVATE: {
    limit: 6,
    running: 0,
    queued: [],
  },
  PUBLIC: {
    limit: 5,
    running: 0,
    queued: [],
  },
}

export type UploadSummary = {
  totalBytes: number
  completedBytes: number
  remainingUploadBytes: number
  uploadingSpeedBytesPerSecond: number
  uploadingCount: number
  counts: Record<UploadFilter, number>
}

type UploadStoreState = {
  filter: UploadFilter
  items: UploadItem[]
  /** Derived from `items`; update only via the summary helpers below. */
  summary: UploadSummary
  clearUploads: () => void
  enqueueUploads: (input: EnqueueUploadsInput) => void
  retryFailed: () => void
  retryUpload: (id: string) => void
  cancelUpload: (id: string) => void
  setFilter: (filter: UploadFilter) => void
}

type ItemSummaryContribution = {
  totalBytes: number
  completedBytes: number
  remainingUploadBytes: number
  uploadingSpeedBytesPerSecond: number
  uploadingCount: number
  countKey: Exclude<UploadFilter, 'all'>
}

const emptySummary = (): UploadSummary => ({
  totalBytes: 0,
  completedBytes: 0,
  remainingUploadBytes: 0,
  uploadingSpeedBytesPerSecond: 0,
  uploadingCount: 0,
  counts: {
    all: 0,
    ongoing: 0,
    completed: 0,
    failed: 0,
  },
})

function getItemCompletedBytes(item: UploadItem) {
  return item.hashBytes + item.uploadBytes
}

function getItemTotalBytes(item: UploadItem) {
  return item.fileBytes * 2
}

function isOngoingStatus(status: UploadStatus) {
  return status === 'QUEUED' || status === 'PREPARING' || status === 'UPLOADING'
}

function getCountKey(status: UploadStatus): Exclude<UploadFilter, 'all'> {
  if (status === 'SUCCESS') {
    return 'completed'
  }

  if (status === 'ERROR') {
    return 'failed'
  }

  return 'ongoing'
}

function getItemSummaryContribution(item: UploadItem): ItemSummaryContribution {
  return {
    totalBytes: getItemTotalBytes(item),
    completedBytes: getItemCompletedBytes(item),
    remainingUploadBytes: isOngoingStatus(item.status)
      ? Math.max(0, item.fileBytes - item.uploadBytes)
      : 0,
    uploadingSpeedBytesPerSecond:
      item.status === 'UPLOADING' ? item.speedBytesPerSecond : 0,
    uploadingCount: item.status === 'UPLOADING' ? 1 : 0,
    countKey: getCountKey(item.status),
  }
}

function addToSummary(summary: UploadSummary, item: UploadItem): UploadSummary {
  const contribution = getItemSummaryContribution(item)
  const counts = { ...summary.counts }

  counts.all += 1
  counts[contribution.countKey] += 1

  return {
    totalBytes: summary.totalBytes + contribution.totalBytes,
    completedBytes: summary.completedBytes + contribution.completedBytes,
    remainingUploadBytes:
      summary.remainingUploadBytes + contribution.remainingUploadBytes,
    uploadingSpeedBytesPerSecond:
      summary.uploadingSpeedBytesPerSecond +
      contribution.uploadingSpeedBytesPerSecond,
    uploadingCount: summary.uploadingCount + contribution.uploadingCount,
    counts,
  }
}

function subtractFromSummary(
  summary: UploadSummary,
  item: UploadItem,
): UploadSummary {
  const contribution = getItemSummaryContribution(item)
  const counts = { ...summary.counts }

  counts.all -= 1
  counts[contribution.countKey] -= 1

  return {
    totalBytes: summary.totalBytes - contribution.totalBytes,
    completedBytes: summary.completedBytes - contribution.completedBytes,
    remainingUploadBytes:
      summary.remainingUploadBytes - contribution.remainingUploadBytes,
    uploadingSpeedBytesPerSecond:
      summary.uploadingSpeedBytesPerSecond -
      contribution.uploadingSpeedBytesPerSecond,
    uploadingCount: summary.uploadingCount - contribution.uploadingCount,
    counts,
  }
}

function replaceItemInSummary(
  summary: UploadSummary,
  previous: UploadItem,
  next: UploadItem,
): UploadSummary {
  const before = getItemSummaryContribution(previous)
  const after = getItemSummaryContribution(next)
  const counts = { ...summary.counts }

  if (before.countKey !== after.countKey) {
    counts[before.countKey] -= 1
    counts[after.countKey] += 1
  }

  return {
    totalBytes: summary.totalBytes + (after.totalBytes - before.totalBytes),
    completedBytes:
      summary.completedBytes + (after.completedBytes - before.completedBytes),
    remainingUploadBytes:
      summary.remainingUploadBytes +
      (after.remainingUploadBytes - before.remainingUploadBytes),
    uploadingSpeedBytesPerSecond:
      summary.uploadingSpeedBytesPerSecond +
      (after.uploadingSpeedBytesPerSecond -
        before.uploadingSpeedBytesPerSecond),
    uploadingCount:
      summary.uploadingCount + (after.uploadingCount - before.uploadingCount),
    counts,
  }
}

export function createUploadSummary(items: UploadItem[]): UploadSummary {
  return items.reduce(addToSummary, emptySummary())
}

function getUploadTimestamp() {
  return globalThis.performance?.now() ?? Date.now()
}

function calculateSpeedBytesPerSecond({
  byteDelta,
  elapsedMs,
  previousSpeed,
}: {
  byteDelta: number
  elapsedMs: number
  previousSpeed: number
}) {
  if (byteDelta <= 0 || elapsedMs <= 0) {
    return previousSpeed
  }

  return Math.round((byteDelta * 1000) / elapsedMs)
}

function clampFileProgress(completed: number, total: number) {
  return Math.min(total, Math.max(0, completed))
}

function getUploadFilePath(apiPath: string, fileMeta: FileMeta) {
  return createPath(
    apiPath,
    fileMeta.relativePath ||
      fileMeta.file.webkitRelativePath ||
      fileMeta.file.name,
  )
}

const isTerminalStatus = (status: UploadStatus) =>
  status === 'SUCCESS' || status === 'ERROR'

function canPatchUploadItem(item: UploadItem) {
  return !isTerminalStatus(item.status) && item.status !== 'QUEUED'
}

const mapConflictActionToHandler = (
  action: ConflictResponse['action'],
): AllocateConflictHandlerType => {
  if (action === 'overwrite') {
    return AllocateConflictHandler.CREATE_NEW_REVISION
  }

  if (action === 'rename') {
    return AllocateConflictHandler.CREATE_NEW_FILE
  }

  return AllocateConflictHandler.REJECT_CONFLICTS
}

function clearFromQueued(id: string) {
  Object.values(scopeRuntimes).forEach(runtime => {
    runtime.queued = runtime.queued.filter(queuedId => queuedId !== id)
  })
}

function getConflictType(error: AppError): FileConflictType | null {
  const code = error.errors.at(0)?.code

  if (code === ErrorType.FILE_ONTO_FILE) {
    return 'FILE_ONTO_FILE'
  }

  if (code === ErrorType.FILE_ONTO_FOLDER) {
    return 'FILE_ONTO_FOLDER'
  }

  if (code === ErrorType.FOLDER_ONTO_FILE) {
    return 'FOLDER_ONTO_FILE'
  }

  return null
}

function resetForRetry(item: UploadItem): UploadItem {
  return {
    ...item,
    status: 'QUEUED',
    hashBytes: 0,
    uploadBytes: 0,
    speedBytesPerSecond: 0,
    error: null,
    abortController: null,
    conflictHandler: AllocateConflictHandler.REJECT_CONFLICTS,
  }
}

export const useUploadStore = create<UploadStoreState>((set, get) => ({
  filter: 'all',
  items: [],
  summary: emptySummary(),
  clearUploads: () => {
    const items = get().items

    if (!items.length) {
      return
    }

    items.forEach(item => {
      clearFromQueued(item.id)
      item.abortController?.abort()
    })

    uploadConflicts.clear()
    set(state => ({
      ...state,
      filter: 'all',
      items: [],
      summary: emptySummary(),
    }))
  },
  enqueueUploads: ({
    apiPath,
    files,
    formatErrorMessage,
    onCompleted,
    openItem,
    scope,
    token,
  }) => {
    if (!files.length) {
      return
    }

    const queuedAt = Date.now()
    const activeUploads = get().items.filter(
      item => !isTerminalStatus(item.status),
    ).length

    if (activeUploads + files.length > 1) {
      uploadConflicts.hasMultiple = true
    }

    const nextItems: UploadItem[] = files.map(fileMeta => {
      const fileName = fileMeta.file.name.split('/').slice(-1)[0]
      const fileBytes = fileMeta.file.size
      const filePath = getUploadFilePath(apiPath, fileMeta)
      return {
        id: nextXid(),
        name: fileName,
        apiPath,
        abortController: null,
        conflictHandler:
          scope === 'PUBLIC'
            ? AllocateConflictHandler.CREATE_NEW_REVISION
            : AllocateConflictHandler.REJECT_CONFLICTS,
        error: null,
        fileBytes,
        formatErrorMessage,
        fileMeta,
        filePath,
        hashBytes: 0,
        onCompleted,
        openItem,
        queuedAt,
        scope,
        speedBytesPerSecond: 0,
        status: 'QUEUED' as const,
        token,
        uploadBytes: 0,
      }
    })

    set(state => ({
      ...state,
      items: [...state.items, ...nextItems],
      summary: nextItems.reduce(addToSummary, state.summary),
    }))

    nextItems.forEach(item => {
      queueUpload(item.id, item.scope)
    })
  },
  retryFailed: () => {
    get()
      .items.filter(item => item.status === 'ERROR')
      .forEach(item => {
        get().retryUpload(item.id)
      })
  },
  retryUpload: id => {
    const current = getUpload(id)

    if (!current || current.status !== 'ERROR') {
      return
    }

    const item = patchUpload(id, resetForRetry)

    if (!item) {
      return
    }

    queueUpload(item.id, item.scope)
  },
  cancelUpload: id => {
    clearFromQueued(id)

    const current = getUpload(id)

    if (!current) {
      return
    }

    current.abortController?.abort()
    removeUploadFromState(id)
    maybeClearConflicts()
  },
  setFilter: filter => {
    set(state => (state.filter === filter ? state : { ...state, filter }))
  },
}))

function patchUpload(
  id: string,
  update: (item: UploadItem) => UploadItem,
): UploadItem | undefined {
  let updated: UploadItem | undefined

  useUploadStore.setState(state => {
    const current = state.items.find(item => item.id === id)

    if (!current) {
      return state
    }

    updated = update(current)

    if (updated === current) {
      return state
    }

    return {
      ...state,
      items: state.items.map(item => (item.id === id ? updated! : item)),
      summary: replaceItemInSummary(state.summary, current, updated),
    }
  })

  return updated
}

function getUpload(id: string) {
  return useUploadStore.getState().items.find(item => item.id === id)
}

function removeUploadFromState(id: string) {
  useUploadStore.setState(state => {
    const current = state.items.find(item => item.id === id)

    if (!current) {
      return state
    }

    const nextItems = state.items.filter(item => item.id !== id)

    return {
      ...state,
      items: nextItems,
      summary: subtractFromSummary(state.summary, current),
      filter: nextItems.length ? state.filter : 'all',
    }
  })
}

function maybeClearConflicts() {
  const hasPending = useUploadStore
    .getState()
    .items.some(item => !isTerminalStatus(item.status))

  if (!hasPending) {
    uploadConflicts.clear()
  }
}

async function runUpload(id: string) {
  const current = getUpload(id)

  if (!current || current.status !== 'QUEUED') {
    return
  }

  const controller = new AbortController()
  let conflictHandler = current.conflictHandler

  patchUpload(id, item => ({
    ...item,
    abortController: controller,
    status: 'PREPARING',
    speedBytesPerSecond: 0,
    error: null,
    conflictHandler,
  }))

  let previousLoaded = 0
  let previousTimestamp = 0

  for (;;) {
    try {
      await uploadFile({
        apiPath: current.apiPath,
        conflictHandler,
        file: current.fileMeta,
        signal: controller.signal,
        token: current.token,
        xid: id,
        onHashProgress: completed => {
          patchUpload(id, item => {
            if (!canPatchUploadItem(item) || item.status === 'UPLOADING') {
              return item
            }

            const hashBytes = clampFileProgress(completed, item.fileBytes)

            return {
              ...item,
              status: 'PREPARING',
              hashBytes,
              speedBytesPerSecond: 0,
            }
          })
        },
        onUploadStart: () => {
          previousLoaded = 0
          previousTimestamp = getUploadTimestamp()

          patchUpload(id, item => {
            if (!canPatchUploadItem(item)) {
              return item
            }

            return {
              ...item,
              status: 'UPLOADING',
              hashBytes: item.fileBytes,
              speedBytesPerSecond: 0,
            }
          })
        },
        onUploadProgress: completed => {
          const now = getUploadTimestamp()
          const uploadBytes = clampFileProgress(completed, current.fileBytes)
          const elapsedMs =
            previousTimestamp === 0 ? 0 : now - previousTimestamp
          const byteDelta = Math.max(0, uploadBytes - previousLoaded)

          if (previousTimestamp === 0 || byteDelta > 0) {
            previousTimestamp = now
            previousLoaded = uploadBytes
          }

          patchUpload(id, item => {
            if (!canPatchUploadItem(item)) {
              return item
            }

            const speedBytesPerSecond = calculateSpeedBytesPerSecond({
              byteDelta,
              elapsedMs,
              previousSpeed: item.speedBytesPerSecond,
            })

            return {
              ...item,
              status: 'UPLOADING',
              hashBytes: item.fileBytes,
              uploadBytes,
              speedBytesPerSecond,
            }
          })
        },
      })

      const completed = patchUpload(id, item => ({
        ...item,
        abortController: null,
        status: 'SUCCESS',
        uploadBytes: item.fileBytes,
        hashBytes: item.fileBytes,
        speedBytesPerSecond: 0,
        error: null,
      }))

      completed?.onCompleted?.()
      maybeClearConflicts()
      return
    } catch (error: unknown) {
      if (error instanceof AbortError || controller.signal.aborted) {
        removeUploadFromState(id)
        maybeClearConflicts()
        return
      }

      const appError = handleUnknownError(error)
      const conflictType = getConflictType(appError)

      if (conflictType && current.scope === 'PRIVATE') {
        const response = await uploadConflicts.requestConflictAction(
          current.fileMeta.file.name,
          conflictType,
        )

        if (response.action === 'cancel' || response.action === 'skip') {
          controller.abort()
          removeUploadFromState(id)
          maybeClearConflicts()
          return
        }

        conflictHandler = mapConflictActionToHandler(response.action)

        patchUpload(id, item => ({
          ...item,
          conflictHandler,
          error: null,
          status: 'PREPARING',
        }))

        continue
      }

      patchUpload(id, item => ({
        ...item,
        abortController: null,
        status: 'ERROR',
        speedBytesPerSecond: 0,
        error: {
          message: item.formatErrorMessage?.(appError) ?? appError.message,
          xid: appError.xid,
        },
      }))
      maybeClearConflicts()
      return
    }
  }
}

function pump(scope: UploadScope) {
  const runtime = scopeRuntimes[scope]

  while (runtime.running < runtime.limit && runtime.queued.length > 0) {
    const id = runtime.queued.shift()

    if (!id) {
      continue
    }

    const item = getUpload(id)

    if (!item || item.status !== 'QUEUED') {
      continue
    }

    runtime.running += 1

    void runUpload(id).finally(() => {
      runtime.running = Math.max(0, runtime.running - 1)
      pump(scope)
    })
  }
}

function queueUpload(id: string, scope: UploadScope) {
  const runtime = scopeRuntimes[scope]

  if (!runtime.queued.includes(id)) {
    runtime.queued.push(id)
  }

  pump(scope)
}

export const enqueueUploads = (input: EnqueueUploadsInput) =>
  useUploadStore.getState().enqueueUploads(input)
